import { ECART_DEFAULT_USER_INFO } from '../pdf-cart/config';
import { IChallengePDFConfig, IChallengePresentationConfig } from '../pdf-cart/types/pdfConfig';
import { UserInfo } from '../pdf-cart/types/states';
import { IBrochurePDFConfig } from '../brochure-cart/types/pdfConfig';

const sessionStorage = window.sessionStorage;
const localStorage = window.localStorage;
const SPEEDBUMP_ACTIVE_KEY = 'ameri-speedbump-active';
const PDF_CONFIG_KEY = 'ameri-screenshots';
const PDF_INDEXED_CONFIG_KEY = 'ameri-indexed';
const PDF_INDEXED_CATEGORIES_CONFIG_KEY = 'ameri-indexed-categories';
const PDF_BROCHURE_CONFIG_KEY = 'ameri-brochure';
const PDF_BROCHURE_CATEGORIES_CONFIG_KEY = 'ameri-brochure-categories';
const INVESTMENT_AMOUNT_KEY = 'ameri-investment-amount';
const SKIP_ACCOUNT_SELECTION_KEY = 'ameri-skip-account-selection';
const SURRENDER_CHARGE_SCHEDULE_KEY = 'ameri-surrender-charge-schedule';
const LINK_STORAGE_KEY = 'ameri-pdf-link';
const DEBUG_KEY = 'ameri-tray-debug';
const USER_INFO_KEY = 'ameri-user-info';
const SELECTION_MODE_KEY = 'ameri-selection-mode';
const SELECT_ALL_RATES_KEY = 'ameri-select-all-rates';
export const COMBO_PATH_CONFIG_KEY = 'combo-path';

class StorageService {
    _getFromSessionStorage(key: string): any {
        return JSON.parse(sessionStorage.getItem(key));
    }

    _saveToSessionStorage(key: string, value: any): void {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    _removeFromSessionStorage(key: string): void {
        sessionStorage.removeItem(key);
    }

    _getFromLocalStorage(key: string): any {
        return JSON.parse(localStorage.getItem(key));
    }

    _saveToLocalStorage(key: string, value: any): void {
        localStorage.setItem(key, JSON.stringify(value));
    }

    _removeFromLocalStorage(key: string): void {
        localStorage.removeItem(key);
    }

    get speedBumpActive(): boolean {
        return this._getFromLocalStorage(SPEEDBUMP_ACTIVE_KEY) !== null
            ? this._getFromLocalStorage(SPEEDBUMP_ACTIVE_KEY)
            : true;
    }
    set speedBumpActive(state: boolean) {
        this._saveToLocalStorage(SPEEDBUMP_ACTIVE_KEY, state);
    }

    get challengePDFConfigs(): IChallengePDFConfig[] {
        return this._getFromLocalStorage(PDF_CONFIG_KEY) || [];
    }

    set challengePDFConfigs(configs: IChallengePDFConfig[]) {
        this._saveToLocalStorage(PDF_CONFIG_KEY, configs);
    }

    get indexedPDFConfigs(): IChallengePDFConfig[] {
        return this._getFromLocalStorage(PDF_INDEXED_CONFIG_KEY) || [];
    }

    set indexedPDFConfigs(configs: IChallengePDFConfig[]) {
        this._saveToLocalStorage(PDF_INDEXED_CONFIG_KEY, configs);
    }

    get indexedPDFConfigsCategories(): string[] {
        return this._getFromLocalStorage(PDF_INDEXED_CATEGORIES_CONFIG_KEY) || [];
    }

    set indexedPDFConfigsCategories(configs: string[]) {
        this._saveToLocalStorage(PDF_INDEXED_CATEGORIES_CONFIG_KEY, configs);
    }

    get brochurePDFConfigs(): IBrochurePDFConfig[] {
        return this._getFromLocalStorage(PDF_BROCHURE_CONFIG_KEY) || [];
    }

    set brochurePDFConfigs(configs: IBrochurePDFConfig[]) {
        this._saveToLocalStorage(PDF_BROCHURE_CONFIG_KEY, configs);
    }

    get brochurePDFConfigsCategories(): string[] {
        return this._getFromLocalStorage(PDF_BROCHURE_CATEGORIES_CONFIG_KEY) || [];
    }

    set brochurePDFConfigsCategories(configs: string[]) {
        this._saveToLocalStorage(PDF_BROCHURE_CATEGORIES_CONFIG_KEY, configs);
    }

    get challengePresentationConfigs(): IChallengePresentationConfig[] {
        return this._getFromSessionStorage(COMBO_PATH_CONFIG_KEY) || [];
    }

    set challengePresentationConfigs(configs: IChallengePresentationConfig[]) {
        this._saveToSessionStorage(COMBO_PATH_CONFIG_KEY, configs);
    }

    get investmentAmount(): number {
        return this._getFromSessionStorage(INVESTMENT_AMOUNT_KEY) || 100000;
    }

    set investmentAmount(amount: number) {
        this._saveToSessionStorage(INVESTMENT_AMOUNT_KEY, amount);
    }

    get surrenderChargeSchedule(): number {
        return this._getFromSessionStorage(SURRENDER_CHARGE_SCHEDULE_KEY) || 6;
    }

    set surrenderChargeSchedule(amount: number) {
        this._saveToSessionStorage(SURRENDER_CHARGE_SCHEDULE_KEY, amount);
    }

    get userInfo(): UserInfo {
        return this._getFromSessionStorage(USER_INFO_KEY) || ECART_DEFAULT_USER_INFO;
    }

    set userInfo(userInfo: UserInfo) {
        this._saveToSessionStorage(USER_INFO_KEY, userInfo);
    }

    get selectionMode(): string {
        return this._getFromSessionStorage(SELECTION_MODE_KEY) || 'indexed-accounts';
    }

    set selectionMode(selectionMode: string) {
        this._saveToSessionStorage(SELECTION_MODE_KEY, selectionMode);
    }

    get selectAllRates(): boolean {
        return this._getFromSessionStorage(SELECT_ALL_RATES_KEY) || false;
    }

    set selectAllRates(selectAllRates: boolean) {
        this._saveToSessionStorage(SELECT_ALL_RATES_KEY, selectAllRates);
    }

    clearPdfConfig() {
        this._removeFromLocalStorage(PDF_CONFIG_KEY);
    }

    clearPdfIndexedConfig() {
        this._removeFromLocalStorage(PDF_INDEXED_CONFIG_KEY);
    }

    clearBrochurePdfConfig() { 
        this._removeFromLocalStorage(PDF_BROCHURE_CONFIG_KEY);
    }

    clearPresentationConfig() {
        this._removeFromSessionStorage(COMBO_PATH_CONFIG_KEY);
    }

    get pdfLink(): string {
        return this._getFromSessionStorage(LINK_STORAGE_KEY) || undefined;
    }

    set pdfLink(link: string) {
        this._saveToSessionStorage(LINK_STORAGE_KEY, link);
    }

    clearPdfLink() {
        this._removeFromSessionStorage(LINK_STORAGE_KEY);
    }

    get debugActive(): boolean {
        return this._getFromLocalStorage(DEBUG_KEY) === '1' ? true : false;
    }
}

const storageService = new StorageService();

export default storageService;
